'use client';

import * as React from 'react';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from './utils';

import type { HTMLMotionProps } from 'framer-motion';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        'fixed inset-0 z-[60]',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
        className,
      )}
      {...props}
    />
  );
});
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogContentProps = {
  closeIcon?: React.ReactNode;
  closeIconClassName?: string;
  closeIconDisabled?: boolean;
  showCloseIcon?: boolean;
  overlayProps?: React.ComponentProps<typeof DialogPrimitive.Overlay>;
  motionProps?: HTMLMotionProps<'div'>;
} & React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>;

const DialogContent = React.forwardRef<React.ComponentRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  (
    { className, children, closeIcon, closeIconClassName, closeIconDisabled, showCloseIcon = true, overlayProps, motionProps, ...props },
    ref,
  ) => {
    return (
      <DialogOverlay
        aria-label="dialog-overlay"
        className={cn(
          'fixed grid top-0 left-0 bottom-0 right-0 place-items-center',
          'overflow-y-auto',
          'py-[40px] px-[16px]',
          'bg-overlay backdrop-blur-sm',
        )}
        style={{ scrollbarGutter: 'stable', touchAction: 'auto' }}
        {...overlayProps}
      >
        <motion.div {...motionProps} className="flex w-full md:w-auto justify-center md:justify-start min-w-0">
          <DialogPrimitive.Content
            ref={ref}
            className={cn(
              'relative w-full max-w-lg',
              'bg-layer-1 text-text-primary',
              'ring-[1px] ring-inset ring-border',
              'rounded-lg shadow-xl',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
              'scrollbar-hide',
              className,
            )}
            style={{ touchAction: 'auto' }}
            {...props}
          >
            {children}
            {showCloseIcon && (
              <DialogPrimitive.Close
                disabled={closeIconDisabled}
                aria-label="Close"
                className={cn(
                  'absolute right-4 top-4 inline-flex size-7 items-center justify-center',
                  'rounded-md text-text-secondary',
                  'hover:bg-background-hover hover:text-text-primary',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus',
                  'disabled:pointer-events-none disabled:opacity-40',
                  'transition-colors',
                  closeIconClassName,
                )}
              >
                {closeIcon ?? <X className="size-4" strokeWidth={2.25} />}
              </DialogPrimitive.Close>
            )}
          </DialogPrimitive.Content>
        </motion.div>
      </DialogOverlay>
    );
  },
);

DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('px-6 py-5', className)} {...props} />
);
DialogBody.displayName = 'DialogBody';

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-y-1 px-6 pt-6 pb-2 text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse gap-2 px-6 pb-6 pt-2',
      'md:flex-row md:justify-end md:items-center',
      'border-t border-border md:border-t-0',
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn('text-left text-lg font-semibold leading-6 text-text-primary', className)} {...props} />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn('text-left text-sm text-text-secondary', className)} {...props} />
));

DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
